import * as React from "react";
import { TableCell, TableRow, Grid, Typography, Checkbox } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { KeyValue, Query } from "../../../interfaces";
import { ApiBaseUrl } from "../../../config";
import { fetchData } from "../../../common/services/ApiClient";
import ModalDialog from "../../ModalDialog";
import OfficeMapDisplay from "../../OfficeMapDisplay";
import {
  reduceObj,
  serviceTypeLookup,
} from "../../../common/services/Utilities";
import generateBranchOfficeDepositsApiUrl from "./BranchOfficeDepositsUtilities";
interface Props {
  appState: Query;
  countyName: string;
  stateName: string;
  fromBHC?: boolean;
  cert?: string;
  checked?: KeyValue;
  setChecked?: (checked: KeyValue) => void;
}
const useStyles = makeStyles(theme => ({
  rowIndent: {
    "&.MuiTableCell-root.MuiTableCell-body": {
      textAlign: "left",
      paddingLeft: 70,
    },
  },
  tableRow: {
    "cursor": "pointer",
    "&.MuiTableRow-root:hover, &.MuiTableRow-root:focus-visible": {
      background: "rgba(0, 0, 0, 0.04)",
    },
  },
  rowIndentBhc: {
    "&.MuiTableCell-root.MuiTableCell-body": {
      textAlign: "left",
      paddingLeft: 90,
    },
  },
}));
export default function SODAddressList({
  appState,
  countyName,
  stateName,
  fromBHC,
  cert,
  checked,
  setChecked,
}: Props) {
  const [addressData, setAddressData] = React.useState<KeyValue[]>([]);
  const classes = useStyles();
  const [displayBranchModal, setDisplayBranchModal] = React.useState(false);
  const [currentData, setCurrentData] = React.useState<KeyValue>({});
  const [initialLoad, setInitialLoad] = React.useState(true);
  const [getLocationsData, setGetLocationsData] = React.useState(true);
  const pgcStep1 = appState?.ui?.pgcStep === "step1";

  React.useEffect(() => {
    if (initialLoad && setChecked && checked && addressData.length > 0) {
      setInitialLoad(false);
      const allUninumsChecked = reduceObj(
        addressData.map(address => {
          return {
            key: `${stateName}_${countyName}_${address.UNINUMBR}`,
            value:
              checked[`${stateName}_${countyName}_${address.UNINUMBR}`] !==
              undefined
                ? {
                    checked:
                      checked[`${stateName}_${countyName}_${address.UNINUMBR}`]
                        ?.checked,
                    count: 1,
                    deposits: address.DEPSUMBR,
                  }
                : {
                    checked: checked[`${stateName}_${countyName}`]?.checked,
                    count: 1,
                    deposits: address.DEPSUMBR,
                  },
          };
        }),
      );

      setChecked({ ...checked, ...allUninumsChecked });
    }
  }, [addressData, checked, countyName, initialLoad, setChecked, stateName]);
  const addressModal = (data: KeyValue) => (
    <ModalDialog
      title={data.NAMEBR}
      subtitle={`Branch Number: ${data.BRNUM}`}
      onExited={() => setCurrentData({})}
      display={displayBranchModal}
      setDisplay={setDisplayBranchModal}
    >
      <Grid container spacing={0}>
        <Grid item xs={10}>
          <OfficeMapDisplay data={data} useBranchAddress />
        </Grid>
        <Grid item xs={2} style={{ textAlign: "center" }}>
          <Typography
            color="primary"
            variant="body1"
            style={{ fontWeight: "bold" }}
          >
            Insured Branch Deposits
          </Typography>
          <Typography
            style={{ fontWeight: "bold", color: "#666666", fontSize: 24 }}
          >
            {data.DEPSUMBR ? data.DEPSUMBR.toLocaleString() : 0}
          </Typography>
          <Typography variant="body1">(Thousands USD)</Typography>
        </Grid>
      </Grid>
    </ModalDialog>
  );
  const certTableColumns = (data: KeyValue, checkedState?: boolean) => {
    const onClickProps = {
      onKeyDown: (e: any) => {
        if (e.key === "Enter") {
          setDisplayBranchModal(true);
          setCurrentData(data);
        }
      },
      onClick: () => {
        setDisplayBranchModal(true);
        setCurrentData({
          ...data,
          BRSERTYP: serviceTypeLookup(data.BRSERTYP),
        });
      },
    };
    return (
      <>
        <TableCell {...onClickProps} className={classes.rowIndent}>
          {data.ADDRESBR}
        </TableCell>
        <TableCell {...onClickProps} />
        {appState.ui.partialMerger.length > 0 &&
          appState.filters.cert !== appState.ui.certAcquiring &&
          checked &&
          setChecked &&
          checkedState !== undefined && (
            <TableCell>
              <Checkbox
                id={"checkbox"}
                checked={checkedState}
                onChange={() => {
                  const newState = {
                    ...checked,
                    [`${stateName}_${countyName}_${data.UNINUMBR}`]: {
                      ...checked[`${stateName}_${countyName}_${data.UNINUMBR}`],
                      checked: !checkedState,
                    },
                  };
                  const allCheckState = Object.keys(newState)
                    .map(check =>
                      check.includes(`${stateName}_${countyName}_`)
                        ? newState[check].checked
                        : "",
                    )
                    .filter(data => data !== "");
                  const parentState = allCheckState.every(check => check);
                  const checkboxState = {
                    ...newState,
                    [stateName]: {
                      ...checked[stateName],
                      checked: parentState,
                    },
                    [`${stateName}_${countyName}`]: {
                      ...checked[`${stateName}_${countyName}`],
                      checked: parentState,
                    },
                  };
                  setChecked(checkboxState);
                }}
              />
            </TableCell>
          )}
        <TableCell {...onClickProps}>{data.CITY2BR}</TableCell>
        <TableCell {...onClickProps}>{data.ZIPBR}</TableCell>
        <TableCell {...onClickProps} />
        <TableCell {...onClickProps}>{data.UNINUMBR}</TableCell>
        <TableCell {...onClickProps}>{data.BRNUM}</TableCell>
        <TableCell {...onClickProps}>
          {serviceTypeLookup(data.BRSERTYP)}
        </TableCell>
        <TableCell {...onClickProps}>
          {pgcStep1 ? data?.ESTYMD : data.BRCENM}
        </TableCell>
        <TableCell
          {...onClickProps}
          style={{ textAlign: pgcStep1 ? "center" : "right" }}
        >
          {pgcStep1 ? data?.ACQDATE : data.DEPSUMBR.toLocaleString()}
        </TableCell>
      </>
    );
  };
  const bhcsTableColumns = (data: KeyValue) => {
    const onClickProps = {
      onKeyDown: (e: any) => {
        if (e.key === "Enter") {
          setDisplayBranchModal(true);
          setCurrentData(data);
        }
      },
      onClick: () => {
        setDisplayBranchModal(true);
        setCurrentData({
          ...data,
          BRSERTYP: serviceTypeLookup(data.BRSERTYP),
        });
      },
    };
    return (
      <>
        <TableCell {...onClickProps} className={classes.rowIndentBhc}>
          {data.NAMEBR}
        </TableCell>
        <TableCell />
        <TableCell {...onClickProps}>{data.ADDRESBR}</TableCell>
        <TableCell {...onClickProps}>{data.CITY2BR}</TableCell>
        <TableCell {...onClickProps}>{data.ZIPBR}</TableCell>
        <TableCell />
        <TableCell {...onClickProps}>{data.UNINUMBR}</TableCell>
        <TableCell {...onClickProps}>{data.BKCLASS}</TableCell>
        <TableCell {...onClickProps}>{data.BRNUM}</TableCell>
        <TableCell {...onClickProps}>
          {serviceTypeLookup(data.BRSERTYP)}
        </TableCell>
        <TableCell {...onClickProps}>{data.BRCENM}</TableCell>
        <TableCell {...onClickProps} style={{ textAlign: "right" }}>
          {data.DEPSUMBR.toLocaleString()}
        </TableCell>
      </>
    );
  };
  const Address = (data: KeyValue, int: number) => {
    return (
      <TableRow
        tabIndex={0}
        className={`${classes.tableRow}`}
        key={`${data.ADDRESBR}_${int}`}
      >
        {fromBHC
          ? bhcsTableColumns(data)
          : certTableColumns(
              data,
              checked &&
                checked[`${stateName}_${countyName}_${data.UNINUMBR}`]?.checked,
            )}
      </TableRow>
    );
  };

  const marketSelectionIndex = appState.filters.stateNames.indexOf(stateName);
  React.useEffect(() => {
    if (countyName) {
      let isMounted = true;
      const bhcCert = cert ? { cert } : {};
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/sod`,
          generateBranchOfficeDepositsApiUrl(
            {
              ...appState,
              filters: {
                ...appState.filters,
                stalpbr: stateName,
                county: countyName,
                ...bhcCert,
              },
            },
            undefined,
            undefined,
            appState.ui.marketSelection[marketSelectionIndex],
            undefined,
            undefined,
            "BRNUM",
            "ASC",
          ),
        )
          .then(response => {
            if (isMounted) {
              setAddressData(response.data);
            }
          })
          .catch((error: Error) => {
            console.error(error);
          });
      })();
      return () => {
        isMounted = false;
      };
    }
  }, [
    appState,
    appState.filters,
    appState.search,
    appState.ui.marketSelection,
    cert,
    countyName,
    marketSelectionIndex,
    stateName,
  ]);

  React.useEffect(() => {
    if (addressData.length > 0 && pgcStep1 && getLocationsData) {
      let isMounted = true;
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/locations?filters=CERT:${
            appState.filters.cert
          } AND STALP:"${stateName}" AND COUNTY:"${countyName}"&sort_by=OFFNUM&sort_order=ASC&limit=10000&offset=0`,
        )
          .then(response => {
            if (isMounted) {
              const newData = response.data;
              const updatedData = [...addressData];
              newData.forEach((newItem: KeyValue) => {
                const matchIndex = updatedData.findIndex(
                  item => item.BRNUM === newItem.OFFNUM,
                );
                if (matchIndex !== -1) {
                  updatedData[matchIndex] = {
                    ...updatedData[matchIndex],
                    ...newItem,
                  };
                }
              });
              setAddressData(updatedData);
              setGetLocationsData(false);
            }
          })
          .catch(error => {
            console.error(`${error.name} : ${error.message}`);
          });
      })();
      return () => {
        isMounted = false;
      };
    }
  }, [
    addressData,
    appState.filters.cert,
    countyName,
    getLocationsData,
    pgcStep1,
    stateName,
  ]);
  return (
    <>
      {addressModal(currentData)}
      {addressData.map((addressData: KeyValue, int: number) => {
        return Address(addressData, int);
      })}
    </>
  );
}
